import { ThemeProvider, Toaster, TooltipProvider } from "@fleetminder/ui";

import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { RouterProvider } from "react-router-dom";

import { createAppRouter } from "./router.js";
import {
  isAuthorizationFailure,
  SessionProvider,
  sessionQueryKey,
} from "./session.js";

/**
 * Any request can be the one that discovers the session has expired or been
 * revoked. Re-reading the session turns that into a single redirect through
 * the route guard, rather than each screen inventing its own recovery.
 */
function handleQueryFailure(error: unknown, queryKey: readonly unknown[]) {
  if (!isAuthorizationFailure(error) || queryKey[0] === "session") {
    return;
  }

  void queryClient.invalidateQueries({ queryKey: sessionQueryKey });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error) =>
        !isAuthorizationFailure(error) && failureCount < 1,
      staleTime: 30_000,
    },
  },
  mutationCache: new MutationCache({
    onError: (error) => handleQueryFailure(error, []),
  }),
  queryCache: new QueryCache({
    onError: (error, query) => handleQueryFailure(error, query.queryKey),
  }),
});
const router = createAppRouter();

export function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <SessionProvider>
          <TooltipProvider delayDuration={200}>
            <RouterProvider router={router} />
            <Toaster closeButton position="bottom-right" richColors />
          </TooltipProvider>
        </SessionProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
